import type { ReactNode } from "react";
import { tw } from "../../styling";

type Props = {
	children: React.ReactNode;
	italic?: boolean;
	center?: boolean;
	size?: keyof typeof headingSizes;
	id?: string;
	className?: string;
};

const headingSizes = {
	h1: tw`tablet:text-4xl text-3xl`,
	h2: tw`tablet:text-3xl text-2xl`,
	h3: tw`tablet:text-2xl text-xl`,
	h4: tw`tablet:text-xl text-lg`,
	lg: tw`text-lg`,
	base: tw`text-base`,
	xxl: tw`text-8xl`,
};

type HeadingProps = {
	component: "h1" | "h2" | "h3" | "h4";
	children: ReactNode;
	italic?: boolean;
	className?: React.ComponentProps<"div">["className"];
	size?: keyof typeof headingSizes;
};

export const Heading = ({
	component: Component,
	children,
	italic,
	size,
	className,
}: HeadingProps) => (
	<Component className={tw("font-bold", className, size && headingSizes[size], italic && "italic")}>
		{children}
	</Component>
);

const H1 = ({ children, center = false, italic = false, size = "h1", className, id }: Props) => (
	<h1
		id={id}
		className={tw(
			"font-bold",
			"text-text-headline",
			headingSizes[size],
			italic && "italic",
			center && "text-center",
			className,
		)}
	>
		{children}
	</h1>
);

const H2 = ({ children, center = false, italic = false, size = "h2", className, id }: Props) => (
	<h2
		id={id}
		className={tw(
			"font-bold",
			"text-text-headline",
			headingSizes[size],
			italic && "italic",
			center && "text-center",
			className,
		)}
	>
		{children}
	</h2>
);
const H3 = ({ children, center = false, italic = false, size = "h3", className, id }: Props) => (
	<h3
		id={id}
		className={tw(
			"font-bold",
			headingSizes[size],
			italic && "italic",
			center && "text-center",
			"text-text-headline",
			className,
		)}
	>
		{children}
	</h3>
);
const H4 = ({ children, center = false, italic = false, size = "h4", className, id }: Props) => (
	<h4
		id={id}
		className={tw(
			"font-bold",
			"text-text-headline",
			headingSizes[size],
			italic && "italic",
			center && "text-center",
			className,
		)}
	>
		{children}
	</h4>
);

Heading.H1 = H1;
Heading.H2 = H2;
Heading.H3 = H3;
Heading.H4 = H4;
